import React, {useState, useEffect} from "react";
import SuiButton from '../../../../components/SoftButton';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Typography,
    Box,
    FormControlLabel,
    IconButton,
    Tooltip,
    CircularProgress,
    Checkbox, useTheme, useMediaQuery
} from "@mui/material";
import {getServicesToRegenerate, regenerateSelectedServices} from "../../../../axios-client";
import servicesTranslations from '../../../../constants/servicesTrasnlations.json';
import {useTranslation} from 'react-i18next';
import PropTypes from "prop-types";
import i18n from "../../../../config/i18n";
import SnackbarNotification from "../../../../reusableComponents/Snackbar/SnackbarNotification";
import {io} from 'socket.io-client';
import {RotateCcw} from "lucide-react";
import PageSelector from "./PageSelector";

const RegenerateServicesPopup = ({digitizationId, currentPageNum, numPages, fetchData}) => {
    const {t} = useTranslation(['regenerateServices']);
    const lang = i18n.language;
    const direction = i18n.dir();
    const [open, setOpen] = useState(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [pageCustom, setPageCustom] = useState("");
    const [errors, setErrors] = useState({custom: ""});
    const [isValid, setIsValid] = useState(true);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        snackbarMessage: "",
        severity: "",

    });

    const isAudioSelected = selectedServices.some(id => {
        const service = serviceOptions.find(option => option.id === id);
        return service?.requiresPageSelection;
    });
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const isTablet = useMediaQuery(theme.breakpoints.down("md"))

    useEffect(() => {

        if (digitizationId) {
            const baseURL = window?.appConfig?.REACT_APP_SOCKET_SERVER || process.env.REACT_APP_SOCKET_SERVER
            const socket = io(baseURL, {
                path: "/socket.io"
            });
            const eventName = `service_update-${digitizationId}`;
            socket.on(eventName, (res) => {

                if (res.status === 'Success' || res.status === 'Completed') {
                    setNotification({
                        show: true,
                        snackbarMessage: res?.message,
                        severity: "success",
                    });
                } else {
                    setNotification({
                        show: true,
                        snackbarMessage: res?.message,
                        severity: "error",
                    });
                }

                if (res.status === 'Completed') {
                    fetchData();
                }
            });

            return () => {
                socket.off(`service_update-${digitizationId}`);
            };
        }
    }, [digitizationId]);

    useEffect(() => {
        if (digitizationId) {
            getServicesToRegenerate(digitizationId).then((res) => {
                setServiceOptions(res.data)
            }).catch((e) => {
                console.error(e)
            })
        }
    }, [digitizationId]);

    const toggleDialog = () => {
        setOpen((prev) => !prev);
        if (open) {
            setSelectedServices([]);
        }
    };

    const handleServiceToggle = (serviceId, serviceName) => {
        setSelectedServices(prev =>
            prev.filter(obj => obj.serviceId === serviceId).length > 0
                ? prev.filter(obj => obj.serviceId !== serviceId)
                : [...prev, {serviceId: serviceId, serviceName: serviceName, selectedOption: 'current', pageNums: currentPageNum.toString(), isValid: true}]
        );
    };

    const updateSelectedOptionService = (serviceId, newOption) => {

        let newSelectedServices = [...selectedServices];

        newSelectedServices.find(obj => obj.serviceId === serviceId).selectedOption = newOption;
        newSelectedServices.find(obj => obj.serviceId === serviceId).isValid = true;

        if (newOption == 'all') {
            newSelectedServices.find(obj => obj.serviceId === serviceId).pageNums = ''
        } else {
            newSelectedServices.find(obj => obj.serviceId === serviceId).pageNums = currentPageNum.toString();
        }
        setSelectedServices(newSelectedServices);
    }
    const updatePageRangeService = (serviceId, newPageNums, isValid) => {

        let newSelectedServices = [...selectedServices];

        newSelectedServices.find(obj => obj.serviceId === serviceId).pageNums = newPageNums;
        newSelectedServices.find(obj => obj.serviceId === serviceId).isValid = isValid;

        setSelectedServices(newSelectedServices);
    }
    const handleConfirm = async () => {

        setLoading(true);

        let services = selectedServices.map(
            selectedService => ({
                service_id: selectedService.serviceId,
                page_nums: selectedService.pageNums,
                service_name: selectedService.serviceName,
            })
        );

        const payload = {
            digitization_id: digitizationId,
            selected_services: services,
        };

        try {
            const res = await regenerateSelectedServices(payload);
            setNotification({
                show: true,
                snackbarMessage: res.data.message,
                severity: "success",
            });
        } catch (err) {
            console.error("Failed to trigger regenerate action:", err);
        } finally {
            setLoading(false);
            toggleDialog();
        }
    };

    return (
        <>
            <Tooltip title={loading ? t("tooltip.regenerating") : 
                serviceOptions.length > 0 ?
                t("tooltip.regenerateServices") : t("tooltip.noServices")}
            >
                <span>
                    <IconButton onClick={toggleDialog} variant="outlined"
                        disabled={serviceOptions.length == 0 || loading}
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    height: { xs: 28, sm: 32 },
                                    width: { xs: 28, sm: 32 },
                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                    color: "#616161",
                                    border: "1px solid #d8d6d6",
                                    borderRadius:"5px",
                                    opacity: 1,
                                    p:1,
                                    "&:hover": {
                                        borderColor: "#757575",
                                        background: "#F5F5F5",
                                    },
                                }}
                                >
                        {loading ? (
                            <CircularProgress size={18} color="inherit"/>
                        ) : (
                            <RotateCcw size={16} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog open={open} onClose={toggleDialog} maxWidth="xs" fullWidth style={{direction: direction}}>
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        {t("title")}
                    </Typography>
                </DialogTitle>

                <Divider/>

                <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    {serviceOptions.map(service => {
                        const isSelected = selectedServices.filter(obj => obj.serviceId === service.id).length > 0;
                        const serviceKey = Object.keys(servicesTranslations.en).find(
                            key =>
                                servicesTranslations[lang]?.[key] === service.label ||
                                key === service.label
                        );
                        const mappedServiceName = servicesTranslations[lang]?.[serviceKey] || service.label;
                        return (
                            <Box key={service.id} sx={{display: 'flex', flexDirection: 'column', pl: 1}}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleServiceToggle(service.id, service.label)}
                                            sx={{
                                                mr: (direction === 'rtl' ? 0 : 1),
                                                ml: direction === 'rtl' ? 1: 0,
                                            }}
                                        />
                                    }
                                    label={mappedServiceName}
                                />

                                {isSelected && service.requiresPageSelection && (
                                    <PageSelector
                                        option={selectedServices.find(obj => obj.serviceId === service.id).selectedOption}
                                        setOption={(e) => updateSelectedOptionService(service.id, e.target.value)}
                                        pageRange={selectedServices.find(obj => obj.serviceId === service.id).pageNums}
                                        setPageRange={(pageRange, valid) => updatePageRangeService(service.id, pageRange, valid)}
                                        numberOfPage={numPages}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </DialogContent>

                <DialogActions sx={{px: 3, pb: 2}} >
                    <SuiButton variant="contained" color="light" onClick={toggleDialog} disabled={loading}>
                        {t("buttons.cancel")}
                    </SuiButton>
                    <SuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={selectedServices.length === 0 || !selectedServices.every(service => service.isValid) || loading}
                        sx={{ml: 1, mr:  (direction === 'rtl' ? 1 : 0),}}
                    >
                        {loading ? <CircularProgress size={20} color="inherit"/> : t("buttons.confirm")}
                    </SuiButton>
                </DialogActions>
            </Dialog>
            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />
        </>
    );
};

RegenerateServicesPopup.propTypes = {
    digitizationId: PropTypes.number,
    currentPageNum: PropTypes.number,
    numPages: PropTypes.number,
    fetchData: PropTypes.func
};

export default RegenerateServicesPopup;
